import * as React from "react";
import i18n from "i18next";
import { RouteComponentProps } from "react-router";
import EvalWorkflowApi, {
  EvalAiMode,
  EvalPass,
  EvalStudy,
  EvalWorkflow,
  HoveredFromState,
  Marker,
  PassResults,
} from "../../api/EvalWorkflowApi";
import GrammarApi, { ParamType } from "../../api/GrammarApi";
import ImageResultApi, { ImageResult } from "../../api/ImageResultApi";
import { AuthManager } from "../../component/auth/AuthContext";
import GlmId from "../../component/GlmId";
import Tags from "../../component/Tags";
import { aiDisplayPrefGmsk, aiDisplayPrefViewer } from "../../component/viewer/AiDisplayPreferences";
import { AsideCard } from "../../component/viewer/AsideCard";
import { DisplayMode } from "../../component/viewer/DisplayMode";
import Viewer from "../../component/viewer/Viewer";
import { AnnotationToolMode } from "../../component/viewer/Viewport";
import ViewportImage from "../../component/viewer/ViewportImage";
import MessageService from "../../service/MessageService";
import { getParamTypes } from "../../util/getParamTypes";
import { isNullOrUndefined } from "../../util/LangUtil";
import MarkerList from "./Markers/MarkerList";
import { getElementsWithUnfilledParams } from "../../util/getElementsWithUnfilledParams";
import EvalPassResult from "./EvalPassResult";
import { isLabelableImage } from "../../util/ImageUtil";

function getImages(study: EvalStudy) {
  return study.series.flatMap((s) => s.images);
}

function affectLabel(marker: Marker, index: number) {
  return {
    ...marker,
    _label: `${marker.obsType.toUpperCase()} ${index + 1}`,
  };
}

interface ReaderWorkflowProps extends RouteComponentProps {
  authManager: AuthManager;
}

interface ReaderWorkflowState {
  evalCode: string;
  readerWorkflow: EvalWorkflow;
  aiMode: EvalAiMode;
  study?: EvalStudy;
  viewportImages?: Array<ViewportImage>;
  /** current pass **/
  pass?: EvalPass;
  loading: boolean;
  imageResults: Array<ImageResult>;
  imagesPathToSee: Array<string>;
  displayImageResults: boolean; // only for aiMode == GMSK
  paramTypesByObsTypes?: Record<string, ParamType[]>;
  passResults?: PassResults;
}

export default class ReaderWorkflow extends React.Component<ReaderWorkflowProps, ReaderWorkflowState> {
  private evalWorkflowApi: EvalWorkflowApi;
  private imageResultApi: ImageResultApi;
  private passStartTime: Date;
  private grammarApi: GrammarApi;

  constructor(props: ReaderWorkflowProps, context: any) {
    super(props, context);

    this.evalWorkflowApi = EvalWorkflowApi.with(props.authManager.cancelableClient());
    this.imageResultApi = ImageResultApi.with(props.authManager.cancelableClient());
    this.grammarApi = GrammarApi.with(props.authManager.cancelableClient());

    const params = new URLSearchParams(props.location.search);
    const readerWorkflow = params.get("readerWorkflow") as EvalWorkflow;

    if (!this.isIn(readerWorkflow, [EvalWorkflow.RD, EvalWorkflow.RD_AI])) {
      MessageService.error(i18n.t("error.forbiddenRDWorkflow"));
      throw new Error(i18n.t("error.forbiddenRDWorkflow"));
    }

    this.state = {
      evalCode: params.get("evalCode"),
      readerWorkflow: readerWorkflow,
      aiMode: EvalAiMode.VIEWER,
      loading: true,
      displayImageResults: false,
      imagesPathToSee: [],
      imageResults: [],
      paramTypesByObsTypes: {},
    };
  }

  componentDidMount(): void {
    this.nextStudy();
  }

  componentWillUnmount(): void {
    this.evalWorkflowApi.cancel();
  }

  private onKey(evt: React.KeyboardEvent): void {
    if (evt.key === "Enter") {
      this.displayImageResults();
    }
  }

  private isGmskModeWithAi(aiMode: EvalAiMode, evalWorkflow: EvalWorkflow) {
    return aiMode === EvalAiMode.GMSK && evalWorkflow === EvalWorkflow.RD_AI;
  }

  private computeAiDisplayPreferences() {
    if (this.state.aiMode === EvalAiMode.VIEWER) {
      return aiDisplayPrefViewer(this.state.readerWorkflow);
    } else {
      return aiDisplayPrefGmsk(this.state.displayImageResults);
    }
  }

  private initPass(): EvalPass {
    return {
      workflow: this.state.readerWorkflow,
      markers: [],
      observations: null,
      tags: [],
      secDuration: null,
    };
  }

  nextStudy = () => {
    this.setState({ loading: true, passResults: null });

    return this.evalWorkflowApi
      .findNextStudy(this.state.readerWorkflow, this.state.evalCode)
      .then((rsp) => {
        if (rsp.status === 403) {
          this.returnToWelcomePage();
          return;
        }

        const study = rsp.data;

        if (!study) {
          this.setState({ study: null, loading: false });
          return;
        }

        getParamTypes(study.rdParamsByObsType, this.grammarApi, study.obsTypes).then((paramTypesByObsTypes) => {
          this.setState({ paramTypesByObsTypes });
        });

        if (this.isGmskModeWithAi(study.aiMode, this.state.readerWorkflow)) {
          this.imageResultApi
            .queryImageResults(study.studyId)
            .then((imageResults) => {
              this.updateComponentStateAndSelectFirstImage(study, imageResults);
            })
            .catch((err) => {
              if (err.response) {
                console.log(err);
                MessageService.error(i18n.t("error.occurred"));
              }
            });
        } else {
          this.updateComponentStateAndSelectFirstImage(study);
        }

        this.initPassStartTime();
      })
      .catch((err) => {
        this.setState({ loading: false });
        if (err.response) {
          console.log(err);
          MessageService.error(i18n.t("error.occurred"));
        }
      });
  };

  private updateComponentStateAndSelectFirstImage(study: EvalStudy, imageResults: ImageResult[] = []) {
    this.setState(
      {
        study,
        viewportImages: this.getViewportImages(study, [], imageResults),
        pass: this.initPass(),
        loading: false,
        aiMode: study.aiMode,
        displayImageResults: false,
        imageResults,
        imagesPathToSee: this.getImagePaths(study),
      },
      () => this.onImageSelected(this.state.viewportImages[0])
    );
  }

  private getImagePaths(study: EvalStudy): string[] {
    const images: string[] = [];
    study.series.forEach((series) => series.images.forEach((image) => images.push(image.path)));
    return images;
  }

  returnToWelcomePage = () => {
    this.props.history.push("/reader");
  };

  private isIn(workflow: EvalWorkflow, workflows: Array<EvalWorkflow>): boolean {
    return workflows.includes(workflow);
  }

  private refresh(study: EvalStudy, markers: Array<Marker>): void {
    const selectedImgIdx = this.getSelectedImgIdx();
    this.setState(
      {
        viewportImages: this.getViewportImages(
          study,
          markers,
          this.state.displayImageResults ? this.state.imageResults : [],
          selectedImgIdx
        ),
      },
      () => {
        this.onImageSelected(this.state.viewportImages[selectedImgIdx]);
      }
    );
  }

  private getSelectedImgIdx() {
    const idx = this.state.viewportImages?.findIndex((imgElt) => imgElt.selected);
    return Math.max(idx, 0);
  }

  private getViewportImages(
    study: EvalStudy,
    markers: Array<Marker>,
    imageResults: Array<ImageResult> = [],
    selectedImgIdx = 0
  ): Array<ViewportImage> {
    const viewportImages = ViewportImage.fromMarkers(getImages(study), markers);

    ViewportImage.fromImageResults(imageResults).forEach((img) => viewportImages.push(img));

    viewportImages[selectedImgIdx].selected = true;

    return viewportImages;
  }

  private saveMarker(marker: Marker, viewportImage: ViewportImage) {
    if (viewportImage.result) {
      MessageService.error(i18n.t("error.addMarkerOnResult"));
      this.refresh(this.state.study, this.state.pass.markers);
      return;
    }

    if (!isLabelableImage(this.state.study.labelableImageTags, viewportImage.image.tags)) {
      MessageService.error(i18n.t("error.addMarkerOnRestrictTag"));
      this.refresh(this.state.study, this.state.pass.markers);
      return;
    }

    const isNewMarker = !marker._label;

    const markers = isNewMarker
      ? this.state.pass.markers.concat(marker).map(affectLabel)
      : this.state.pass.markers.map((prevMarker, index) => {
          if (prevMarker._label === marker._label) {
            return { ...prevMarker, ...affectLabel(marker, index) };
          }
          return prevMarker;
        });

    this.setState(({ pass }) => ({
      pass: {
        ...pass,
        markers,
      },
    }));

    this.refresh(this.state.study, markers);
  }

  deleteMarker = (marker: Marker) => {
    const markers = this.state.pass.markers.filter((m) => m._label !== marker._label).map(affectLabel);

    this.setState(({ pass }) => ({
      pass: {
        ...pass,
        markers,
      },
    }));

    this.refresh(this.state.study, markers);
  };

  validate = () => {
    if (
      this.state.readerWorkflow === EvalWorkflow.RD_AI &&
      isNullOrUndefined(this.state.pass.secDurationBeforeAiActivation)
    ) {
      MessageService.error(i18n.t("error.unactivatedAI"));
      return;
    }

    if (!this.allResultsSeen()) {
      MessageService.error(i18n.t("error.unseeOriginalImages"));
      return;
    }

    const uncompleteMarkers = getElementsWithUnfilledParams(this.state.pass.markers, this.state.paramTypesByObsTypes);

    if (uncompleteMarkers.length > 0) {
      uncompleteMarkers.forEach((marker) => {
        MessageService.error(i18n.t("error.noParam") + marker._label);
      });
      return;
    }

    const savedPass = {
      ...this.state.pass,
      secDuration: this.computeElapsedSec(),
    };

    this.setState({
      pass: savedPass,
    });

    this.evalWorkflowApi
      .savePass(this.state.evalCode, this.state.study.id, savedPass)
      .then(this.savedPassCallback(savedPass));
  };

  private savedPassCallback(pass: EvalPass) {
    return () => {
      MessageService.info(i18n.t("global.saved"));

      if (this.state.study.displayResultAtEndOfEachPass) {
        return this.evalWorkflowApi.computeResult(this.state.evalCode, this.state.study.id, pass).then((resultStat) => {
          this.setState({ passResults: resultStat });
        });
      }

      this.nextStudy();
    };
  }

  private closePassResultModal() {
    this.nextStudy();
  }

  private initPassStartTime() {
    this.passStartTime = new Date();
  }

  private computeElapsedSec() {
    return Math.round((new Date().getTime() - this.passStartTime.getTime()) / 1000);
  }

  onImageLoaded = () => {
    this.setState({ loading: false });
  };

  onAiRegionActivation = () => {
    // save first ai activation
    if (!this.state.pass.secDurationBeforeAiActivation) {
      this.setState(({ pass }) => ({
        pass: {
          ...pass,
          // Need to "clone" markers not to stay binded to the viewport
          markersBeforeAiActivation: pass.markers.map((marker) => ({ ...marker })),
          secDurationBeforeAiActivation: this.computeElapsedSec(),
        },
      }));
    }
  };

  private displayImageResults() {
    if (this.isGmskModeWithAi(this.state.aiMode, this.state.readerWorkflow) && !this.state.displayImageResults) {
      if (!this.allResultsSeen()) {
        MessageService.error(i18n.t("error.unseeOriginalImagesBeforeAI"));
        return;
      }

      this.setState(
        {
          displayImageResults: true,
        },
        () => {
          this.refresh(this.state.study, this.state.pass.markers);
          this.onAiRegionActivation();
        }
      );
    }
  }

  onImageSelected = (vpImage: ViewportImage) => {
    if (!vpImage.result) {
      this.setState(({ imagesPathToSee, viewportImages }) => ({
        imagesPathToSee: imagesPathToSee.filter((path) => path !== vpImage.path),
        viewportImages: viewportImages.map((image) => {
          if (image.path === vpImage.path) {
            return image.select();
          }
          return image.deselect();
        }),
      }));
    }
  };

  allResultsSeen = () => {
    return this.state.imagesPathToSee.length === 0;
  };

  maxSelectableImageIdx = () => {
    if (this.state.displayImageResults) {
      return this.state.viewportImages.length;
    }
    return this.state.viewportImages.length - this.state.imageResults.length;
  };

  onTagsChanged = (tags: Array<string>) => {
    this.setState(({ pass }) => ({
      pass: {
        ...pass,
        tags,
      },
    }));
  };

  onMarkerEnter = (marker: Marker) => this.onMarkerIO(marker, true, "aside");

  onMarkerLeave = (marker: Marker) => this.onMarkerIO(marker, false, "aside");

  onMarkerIO = (marker: Marker, isIn: boolean, from: HoveredFromState = "viewport") => {
    this.setState(({ pass, viewportImages }) => ({
      pass: {
        ...pass,
        markers: pass.markers.map((mrk) => {
          if (marker._label !== mrk._label) {
            return mrk;
          }

          return {
            ...mrk,
            _hoveredFrom: isIn ? from : null,
          };
        }),
      },
      viewportImages: viewportImages.map((image) => image.hoveredMarker(marker, isIn, from)),
    }));
  };

  render(): React.ReactNode {
    return (
      <>
        <div className="container-fluid">
          {this.state.loading && <p>{i18n.t("global.loading")}</p>}
          {!this.state.loading && !this.state.study && <p>{i18n.t("RD.noMoreCase")}</p>}
        </div>

        {this.state.study && (
          <>
            <Viewer
              study={this.state.study}
              annotationToolMode={AnnotationToolMode.MARKER}
              saveMarker={(marker, image) => this.saveMarker(marker, image)}
              deleteMarker={(marker) => this.deleteMarker(marker)}
              authManager={this.props.authManager}
              images={this.state.viewportImages}
              maxSelectableImageIdx={this.maxSelectableImageIdx()}
              onImageLoaded={this.onImageLoaded}
              display={DisplayMode.readerWorkflow(this.state.readerWorkflow, this.state.aiMode)}
              onAiRegionActivation={() => this.onAiRegionActivation()}
              aiDisplayPreferences={this.computeAiDisplayPreferences()}
              onImageSelected={this.onImageSelected}
              onKey={(evt) => this.onKey(evt)}
              onMarkerIO={(marker, isIn) => this.onMarkerIO(marker, isIn, "viewport")}
              paramTypesByObsTypes={this.state.paramTypesByObsTypes}
              actions={() => (
                <div className="list-inline">
                  <div className="list-inline-item btn-group ">
                    <button className="btn btn-primary" onClick={this.validate} disabled={this.state.loading}>
                      {this.state.loading ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <span>{i18n.t("global.validate")}</span>
                      )}
                    </button>

                    <button className="btn btn-default close-btn" onClick={this.returnToWelcomePage}>
                      {i18n.t("global.close")} <i className="fa fa-times" />
                    </button>
                  </div>
                </div>
              )}
              underSeries={() => (
                <>
                  {this.isGmskModeWithAi(this.state.aiMode, this.state.readerWorkflow) &&
                    !this.state.displayImageResults && (
                      <button
                        className="btn btn-primary btn-block"
                        onClick={() => this.displayImageResults()}
                        title="Show results (Enter)"
                      >
                        {i18n.t("RD.showAIResult")}
                      </button>
                    )}
                </>
              )}
              aside={() => (
                <>
                  {this.state.study.tags?.length > 0 && (
                    <AsideCard title="Tags">
                      <Tags
                        availableTags={this.state.study.tags}
                        tags={this.state.pass?.tags || []}
                        onTagsChanged={this.onTagsChanged}
                      />
                    </AsideCard>
                  )}
                  <AsideCard title="Workflow">
                    {this.state.readerWorkflow === EvalWorkflow.RD && <>{i18n.t("global.withoutAi")}</>}
                    {this.state.readerWorkflow === EvalWorkflow.RD_AI && <>{i18n.t("global.withAi")}</>}
                  </AsideCard>
                  <AsideCard title={i18n.t("global.patient")}>
                    {i18n.t("global.age")} : {this.state.study.patientAge} | {i18n.t("global.sex")}:{" "}
                    {this.state.study.patientSex}
                  </AsideCard>
                  <AsideCard title={i18n.t("global.pass")}>
                    {i18n.t("global.nbMarkers")} : {this.state.pass.markers.length}
                  </AsideCard>
                  <AsideCard title={i18n.t("global.case")}>
                    StudyId : <GlmId value={this.state.study.studyId} />
                  </AsideCard>
                  <MarkerList
                    images={this.state.viewportImages}
                    markers={this.state.pass.markers}
                    obsTypes={this.state.study?.obsTypes}
                    onMarkerEnter={this.onMarkerEnter}
                    onMarkerLeave={this.onMarkerLeave}
                    paramTypesByObsTypes={this.state.paramTypesByObsTypes}
                    onSave={(marker) => this.saveMarker(marker, this.state.viewportImages[this.getSelectedImgIdx()])}
                  />
                </>
              )}
            />
            {this.state.passResults && (
              <EvalPassResult result={this.state.passResults} onClose={() => this.closePassResultModal()} />
            )}
          </>
        )}
      </>
    );
  }
}
